// LLM Provider Definitions
export interface LLMProvider {
    id: string
    name: string
    displayName: string
    icon: string // Identifier for the icon component
    color: string
    requiresApiKey: boolean
    apiKeyLabel: string
    apiKeyPlaceholder: string
    endpoint: string
    models: LLMModel[]
    defaultModel: string
    parameters: LLMParameter[]
    authType: 'bearer' | 'header' | 'query'
    authHeaderName?: string
}

export interface LLMModel {
    id: string
    name: string
    displayName: string
    contextWindow: number
    maxOutputTokens: number
    pricing?: {
        input: number  // per 1M tokens
        output: number // per 1M tokens
    }
    capabilities: {
        vision?: boolean
        functionCalling?: boolean
        streaming?: boolean
        json?: boolean
    }
}

export interface LLMParameter {
    id: string
    name: string
    displayName: string
    type: 'number' | 'boolean' | 'select'
    min?: number
    max?: number
    step?: number
    default: string | number | boolean
    description: string
    options?: { value: string | number | boolean; label: string }[]
}

export interface LLMConfiguration {
    providerId: string
    apiKey: string
    modelId: string
    parameters: Record<string, unknown>
    customEndpoint?: string
    azureConfig?: {
        resourceName: string
        deploymentName: string
        apiVersion: string
    }
}

export const LLM_PROVIDERS: LLMProvider[] = [
    // Dynamic Router
    {
        id: 'auto',
        name: 'auto',
        displayName: '✨ Dynamic Router (Auto)',
        icon: 'sparkles',
        color: '#8B5CF6', // Violet
        requiresApiKey: false, // Uses keys from other providers
        apiKeyLabel: '',
        apiKeyPlaceholder: '',
        endpoint: '', // Dynamic
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            {
                id: 'auto',
                name: 'auto',
                displayName: 'Automático',
                contextWindow: 128000,
                maxOutputTokens: 4096,
                capabilities: { functionCalling: true, streaming: true, json: true }
            }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' }
        ]
    },

    // OpenAI
    {
        id: 'openai',
        name: 'openai',
        displayName: 'OpenAI',
        icon: 'openai',
        color: '#10A37F',
        requiresApiKey: true,
        apiKeyLabel: 'OpenAI API Key',
        apiKeyPlaceholder: 'sk-...',
        endpoint: 'https://api.openai.com/v1/chat/completions',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            {
                id: 'auto',
                name: 'auto',
                displayName: 'Automático',
                contextWindow: 128000,
                maxOutputTokens: 16384,
                pricing: { input: 0, output: 0 }, // Dynamic pricing
                capabilities: { vision: true, functionCalling: true, streaming: true, json: true }
            },
            {
                id: 'gpt-4o',
                name: 'gpt-4o',
                displayName: 'GPT-4o',
                contextWindow: 128000,
                maxOutputTokens: 16384,
                pricing: { input: 5, output: 15 },
                capabilities: { vision: true, functionCalling: true, streaming: true, json: true }
            },
            {
                id: 'gpt-4o-mini',
                name: 'gpt-4o-mini',
                displayName: 'GPT-4o Mini',
                contextWindow: 128000,
                maxOutputTokens: 16384,
                pricing: { input: 0.15, output: 0.6 },
                capabilities: { vision: true, functionCalling: true, streaming: true, json: true }
            },
            {
                id: 'gpt-4-turbo',
                name: 'gpt-4-turbo',
                displayName: 'GPT-4 Turbo',
                contextWindow: 128000,
                maxOutputTokens: 4096,
                pricing: { input: 10, output: 30 },
                capabilities: { vision: true, functionCalling: true, streaming: true, json: true }
            },
            {
                id: 'gpt-3.5-turbo',
                name: 'gpt-3.5-turbo',
                displayName: 'GPT-3.5 Turbo',
                contextWindow: 16385,
                maxOutputTokens: 4096,
                pricing: { input: 0.5, output: 1.5 },
                capabilities: { functionCalling: true, streaming: true, json: true }
            }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 16384, step: 1, default: 4096, description: 'Max tokens to generate' },
            { id: 'top_p', name: 'top_p', displayName: 'Top P', type: 'number', min: 0, max: 1, step: 0.01, default: 1, description: 'Nucleus sampling' },
            { id: 'stream', name: 'stream', displayName: 'Stream', type: 'boolean', default: true, description: 'Stream responses' }
        ]
    },

    // Anthropic (Claude)
    {
        id: 'anthropic',
        name: 'anthropic',
        displayName: 'Anthropic (Claude)',
        icon: 'anthropic',
        color: '#D4A574',
        requiresApiKey: true,
        apiKeyLabel: 'Anthropic API Key',
        apiKeyPlaceholder: 'sk-ant-...',
        endpoint: 'https://api.anthropic.com/v1/messages',
        authType: 'header',
        authHeaderName: 'x-api-key',
        defaultModel: 'auto',
        models: [
            {
                id: 'auto',
                name: 'auto',
                displayName: 'Automático',
                contextWindow: 200000,
                maxOutputTokens: 8192,
                pricing: { input: 0, output: 0 },
                capabilities: { vision: true, functionCalling: true, streaming: true }
            },
            {
                id: 'claude-3-5-sonnet-20241022',
                name: 'claude-3-5-sonnet-20241022',
                displayName: 'Claude 3.5 Sonnet',
                contextWindow: 200000,
                maxOutputTokens: 8192,
                pricing: { input: 3, output: 15 },
                capabilities: { vision: true, functionCalling: true, streaming: true }
            },
            {
                id: 'claude-3-opus-20240229',
                name: 'claude-3-opus-20240229',
                displayName: 'Claude 3 Opus',
                contextWindow: 200000,
                maxOutputTokens: 4096,
                pricing: { input: 15, output: 75 },
                capabilities: { vision: true, functionCalling: true, streaming: true }
            },
            {
                id: 'claude-3-haiku-20240307',
                name: 'claude-3-haiku-20240307',
                displayName: 'Claude 3 Haiku',
                contextWindow: 200000,
                maxOutputTokens: 4096,
                pricing: { input: 0.25, output: 1.25 },
                capabilities: { vision: true, streaming: true }
            }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 1, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 8192, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // Mistral AI
    {
        id: 'mistral',
        name: 'mistral',
        displayName: 'Mistral AI',
        icon: 'mistral',
        color: '#FF7000',
        requiresApiKey: true,
        apiKeyLabel: 'Mistral API Key',
        apiKeyPlaceholder: 'xxx...',
        endpoint: 'https://api.mistral.ai/v1/chat/completions',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 128000, maxOutputTokens: 8192, pricing: { input: 0, output: 0 }, capabilities: { functionCalling: true, streaming: true, json: true } },
            { id: 'mistral-large-latest', name: 'mistral-large-latest', displayName: 'Mistral Large', contextWindow: 128000, maxOutputTokens: 8192, pricing: { input: 2, output: 6 }, capabilities: { functionCalling: true, streaming: true, json: true } },
            { id: 'mistral-medium-latest', name: 'mistral-medium-latest', displayName: 'Mistral Medium', contextWindow: 32000, maxOutputTokens: 8192, pricing: { input: 2.7, output: 8.1 }, capabilities: { streaming: true } },
            { id: 'mistral-small-latest', name: 'mistral-small-latest', displayName: 'Mistral Small', contextWindow: 32000, maxOutputTokens: 8192, pricing: { input: 0.2, output: 0.6 }, capabilities: { streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 1, step: 0.1, default: 0.7, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 32000, step: 1, default: 4096, description: 'Max tokens to generate' },
            { id: 'safe_mode', name: 'safe_mode', displayName: 'Safe Mode', type: 'boolean', default: false, description: 'Enable content moderation' }
        ]
    },

    // Google Gemini
    {
        id: 'google',
        name: 'google',
        displayName: 'Google (Gemini)',
        icon: 'google',
        color: '#4285F4',
        requiresApiKey: true,
        apiKeyLabel: 'Google API Key',
        apiKeyPlaceholder: 'AIza...',
        endpoint: 'https://generativelanguage.googleapis.com/v1beta/models',
        authType: 'query',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 2000000, maxOutputTokens: 8192, pricing: { input: 0, output: 0 }, capabilities: { vision: true, functionCalling: true, streaming: true } },
            { id: 'gemini-1.5-pro', name: 'gemini-1.5-pro', displayName: 'Gemini 1.5 Pro', contextWindow: 2000000, maxOutputTokens: 8192, pricing: { input: 1.25, output: 5 }, capabilities: { vision: true, functionCalling: true, streaming: true } },
            { id: 'gemini-1.5-flash', name: 'gemini-1.5-flash', displayName: 'Gemini 1.5 Flash', contextWindow: 1000000, maxOutputTokens: 8192, pricing: { input: 0.075, output: 0.3 }, capabilities: { vision: true, functionCalling: true, streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'maxOutputTokens', name: 'maxOutputTokens', displayName: 'Max Output Tokens', type: 'number', min: 1, max: 8192, step: 1, default: 2048, description: 'Max tokens to generate' }
        ]
    },

    // Cohere
    {
        id: 'cohere',
        name: 'cohere',
        displayName: 'Cohere',
        icon: 'cohere',
        color: '#39594D',
        requiresApiKey: true,
        apiKeyLabel: 'Cohere API Key',
        apiKeyPlaceholder: 'xxx...',
        endpoint: 'https://api.cohere.ai/v1/chat',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 128000, maxOutputTokens: 4096, pricing: { input: 0, output: 0 }, capabilities: { functionCalling: true, streaming: true } },
            { id: 'command-r-plus', name: 'command-r-plus', displayName: 'Command R+', contextWindow: 128000, maxOutputTokens: 4096, pricing: { input: 3, output: 15 }, capabilities: { functionCalling: true, streaming: true } },
            { id: 'command-r', name: 'command-r', displayName: 'Command R', contextWindow: 128000, maxOutputTokens: 4096, pricing: { input: 0.5, output: 1.5 }, capabilities: { functionCalling: true, streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 5, step: 0.1, default: 0.3, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 4096, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // Meta (Llama) - via Groq or similar as example, or generic
    {
        id: 'meta',
        name: 'meta',
        displayName: 'Meta (Llama)',
        icon: 'meta',
        color: '#0668E1',
        requiresApiKey: true,
        apiKeyLabel: 'Provider API Key (e.g. Groq)',
        apiKeyPlaceholder: 'gsk...',
        endpoint: 'https://api.groq.com/openai/v1/chat/completions', // Example using Groq for Llama
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 8192, maxOutputTokens: 8192, pricing: { input: 0, output: 0 }, capabilities: { functionCalling: true, streaming: true, json: true } },
            { id: 'llama3-70b-8192', name: 'llama3-70b-8192', displayName: 'Llama 3 70B', contextWindow: 8192, maxOutputTokens: 8192, pricing: { input: 0.59, output: 0.79 }, capabilities: { functionCalling: true, streaming: true, json: true } },
            { id: 'llama3-8b-8192', name: 'llama3-8b-8192', displayName: 'Llama 3 8B', contextWindow: 8192, maxOutputTokens: 8192, pricing: { input: 0.05, output: 0.10 }, capabilities: { functionCalling: true, streaming: true, json: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 8192, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // Azure OpenAI
    {
        id: 'azure',
        name: 'azure',
        displayName: 'Azure OpenAI',
        icon: 'azure',
        color: '#0078D4',
        requiresApiKey: true,
        apiKeyLabel: 'Azure API Key',
        apiKeyPlaceholder: 'xxx...',
        endpoint: '', // Dynamic based on resource name
        authType: 'header',
        authHeaderName: 'api-key',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 8192, maxOutputTokens: 4096, pricing: { input: 0, output: 0 }, capabilities: { functionCalling: true, streaming: true } },
            { id: 'gpt-4', name: 'gpt-4', displayName: 'GPT-4 (Azure)', contextWindow: 8192, maxOutputTokens: 4096, capabilities: { functionCalling: true, streaming: true } },
            { id: 'gpt-35-turbo', name: 'gpt-35-turbo', displayName: 'GPT-3.5 Turbo (Azure)', contextWindow: 4096, maxOutputTokens: 4096, capabilities: { functionCalling: true, streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 4096, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // Perplexity
    {
        id: 'perplexity',
        name: 'perplexity',
        displayName: 'Perplexity AI',
        icon: 'perplexity',
        color: '#22B3A8',
        requiresApiKey: true,
        apiKeyLabel: 'Perplexity API Key',
        apiKeyPlaceholder: 'pplx-...',
        endpoint: 'https://api.perplexity.ai/chat/completions',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 32768, maxOutputTokens: 4096, pricing: { input: 0, output: 0 }, capabilities: { streaming: true } },
            { id: 'llama-3-sonar-large-32k-online', name: 'llama-3-sonar-large-32k-online', displayName: 'Llama 3 Sonar Large Online', contextWindow: 32768, maxOutputTokens: 4096, pricing: { input: 1, output: 1 }, capabilities: { streaming: true } },
            { id: 'llama-3-sonar-small-32k-online', name: 'llama-3-sonar-small-32k-online', displayName: 'Llama 3 Sonar Small Online', contextWindow: 32768, maxOutputTokens: 4096, pricing: { input: 0.2, output: 0.2 }, capabilities: { streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 0.2, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 4096, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // AI21 Labs
    {
        id: 'ai21',
        name: 'ai21',
        displayName: 'AI21 Labs',
        icon: 'ai21',
        color: '#000000',
        requiresApiKey: true,
        apiKeyLabel: 'AI21 API Key',
        apiKeyPlaceholder: 'xxx...',
        endpoint: 'https://api.ai21.com/studio/v1/chat/completions',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 256000, maxOutputTokens: 4096, pricing: { input: 0, output: 0 }, capabilities: { streaming: true } },
            { id: 'jamba-instruct', name: 'jamba-instruct', displayName: 'Jamba Instruct', contextWindow: 256000, maxOutputTokens: 4096, pricing: { input: 0.5, output: 0.7 }, capabilities: { streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 1, step: 0.1, default: 0.7, description: 'Controls randomness' },
            { id: 'max_tokens', name: 'max_tokens', displayName: 'Max Tokens', type: 'number', min: 1, max: 4096, step: 1, default: 4096, description: 'Max tokens to generate' }
        ]
    },

    // Hugging Face
    {
        id: 'huggingface',
        name: 'huggingface',
        displayName: 'Hugging Face',
        icon: 'huggingface',
        color: '#FFD21E',
        requiresApiKey: true,
        apiKeyLabel: 'HF Access Token',
        apiKeyPlaceholder: 'hf_...',
        endpoint: 'https://api-inference.huggingface.co/models',
        authType: 'bearer',
        defaultModel: 'auto',
        models: [
            { id: 'auto', name: 'auto', displayName: 'Automático', contextWindow: 8192, maxOutputTokens: 4096, pricing: { input: 0, output: 0 }, capabilities: { streaming: true } },
            { id: 'meta-llama/Meta-Llama-3-8B-Instruct', name: 'meta-llama/Meta-Llama-3-8B-Instruct', displayName: 'Llama 3 8B Instruct', contextWindow: 8192, maxOutputTokens: 4096, capabilities: { streaming: true } },
            { id: 'tiiuae/falcon-7b-instruct', name: 'tiiuae/falcon-7b-instruct', displayName: 'Falcon 7B Instruct', contextWindow: 2048, maxOutputTokens: 2048, capabilities: { streaming: true } }
        ],
        parameters: [
            { id: 'temperature', name: 'temperature', displayName: 'Temperature', type: 'number', min: 0, max: 2, step: 0.1, default: 1, description: 'Controls randomness' },
            { id: 'max_new_tokens', name: 'max_new_tokens', displayName: 'Max New Tokens', type: 'number', min: 1, max: 4096, step: 1, default: 1024, description: 'Max tokens to generate' }
        ]
    }
]
